/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import { SoundingManagerListItem } from './SoundingManagerListItem';
import type { SoundingPanel } from '../../store/types';

interface SoundingsManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onMouseDown: () => void;
  order: number;
  soundingPanelsInOrder: SoundingPanel[];
  onOrderChange: (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ) => void;
  onToggleVisibility: (id: string) => void;
}

export const SoundingsManager = ({
  isOpen,
  onClose,
  onMouseDown,
  order,
  soundingPanelsInOrder,
  onOrderChange,
  onToggleVisibility,
}: SoundingsManagerProps): React.ReactElement => {
  const sortableList = React.useMemo(
    () => soundingPanelsInOrder.map((collection) => ({ ...collection })),
    [soundingPanelsInOrder],
  );

  return (
    <ToolContainerDraggable
      isOpen={isOpen}
      minWidth={300}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      title="Soundings Manager"
    >
      <ReactSortable
        animation={200}
        direction="vertical"
        draggable=".sortable-item"
        handle=".drag-handle"
        list={sortableList}
        setList={onOrderChange}
        style={{ padding: '5px' }}
        tag="div"
      >
        {sortableList.map(({ id, name, isDisplayed }) => (
          <SoundingManagerListItem
            id={id}
            isDisplayed={isDisplayed}
            key={id}
            name={name}
            onToggleVisibility={onToggleVisibility}
          />
        ))}
      </ReactSortable>
    </ToolContainerDraggable>
  );
};
